import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import "./CelestialDetails.css"; // Ensure your CSS is set up for the new layout

function CelestialDetails() {
  const [object, setObject] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [language, setLanguage] = useState("en");
  const [translatedDesc, setTranslatedDesc] = useState("");
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceTranslated, setSelectedVoiceTranslated] = useState("");
  const [polling, setPolling] = useState(true);
  const [intervalMs, setIntervalMs] = useState(3000);
  const [darkMode, setDarkMode] = useState(true);

  const speechRefTranslated = useRef(null);
  const prevObjRef = useRef(null);
  const translateTimerRef = useRef(null);

  // load voices and auto-select based on language
  useEffect(() => {
    const loadVoices = () => {
      const v = window.speechSynthesis.getVoices();
      setVoices(v || []);

      // Auto-select a voice for the currently active language
      const autoSelectVoice = (langCode) => {
        const primaryLang = (langCode || "en").split("-")[0].toLowerCase();
        // Try to find a voice that matches the primary language (e.g., "ta" for "ta-IN")
        const found = v.find(
          (voice) => (voice.lang || "").toLowerCase().startsWith(primaryLang) && voice.name.includes('Google') // Prioritize Google voices if available
        );
        if (found) return found.name;

        // Fallback: just find any voice that matches the primary language
        const fallbackFound = v.find(
            (voice) => (voice.lang || "").toLowerCase().startsWith(primaryLang)
        );
        if (fallbackFound) return fallbackFound.name;

        // If no specific language voice, fallback to first available English voice
        const englishVoice = v.find(
            (voice) => (voice.lang || "").toLowerCase().startsWith('en')
        );
        if (englishVoice) return englishVoice.name;

        return v.length > 0 ? v[0].name : ""; // Absolute last resort
      };

      setSelectedVoiceTranslated(autoSelectVoice(language));
    };

    // Re-run voice loading and selection when language changes
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [language]);

  // Helper: simple deep-ish compare to decide if object changed significantly
  const changedSignificantly = (prev, next) => {
    if (!prev || !next) return true;
    if (prev.name !== next.name) return true;
    const altDiff = Math.abs((prev.altitude || 0) - (next.altitude || 0));
    const azDiff = Math.abs((prev.azimuth || 0) - (next.azimuth || 0));
    return altDiff > 0.5 || azDiff > 0.5; // threshold
  };

  // Map some common constellation aliases/abbreviations to IAU names
  const IAU_MAP = {
    CYG: "Cygnus",
    Cyg: "Cygnus",
    CYGNUS: "Cygnus",
    ORI: "Orion",
    ORION: "Orion",
    TAU: "Taurus",
    SCT: "Scutum",
    LYR: "Lyra", // Added for Vega
    AQR: "Aquarius", // ADDED FOR SATURN
    Aqr: "Aquarius", // ADDED FOR SATURN
    // add more aliases as needed
  };

  const mapToIAU = (raw) => {
    if (!raw) return { full: "Unknown", trimmed: "Unknown" };
    const cleaned = String(raw).trim();
    let fullConstellation = IAU_MAP[cleaned] || IAU_MAP[cleaned.toUpperCase()] || cleaned;

    // Convert to Title Case for better display
    fullConstellation = fullConstellation
      .toLowerCase()
      .split(/\s|_|-/)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");

    let trimmedConstellation = fullConstellation;
    // Attempt to find a 3-letter abbreviation for the full constellation name
    const foundAbbreviation = Object.keys(IAU_MAP).find(key => IAU_MAP[key] === fullConstellation && key.length === 3);
    if (foundAbbreviation) {
      trimmedConstellation = foundAbbreviation;
    } else if (fullConstellation.length > 3) {
      trimmedConstellation = fullConstellation.substring(0,3).toUpperCase(); // Default to first 3 letters for unknown/long constellations
    } else {
        trimmedConstellation = fullConstellation.toUpperCase(); // For short names like "Sun", use its full uppercase form
    }

    return { full: fullConstellation, trimmed: trimmedConstellation };
  };


  const LANG_OPTIONS = [
    { code: "en", label: "English" },
    { code: "ml", label: "Malayalam" },
    { code: "hi", label: "Hindi" },
    { code: "ta", label: "Tamil" },
    { code: "te", label: "Telugu" },
    { code: "kn", label: "Kannada" },
  ];

  const getLangLabel = (code) => {
    const found = LANG_OPTIONS.find((l) => l.code === code);
    return found ? found.label : code;
  };

  // Fetch celestial object from Stellarium + Wikipedia
  const fetchObject = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch("http://localhost:8090/api/objects/info?format=json");
      if (!response.ok) throw new Error(`Stellarium responded ${response.status}`);
      const data = await response.json();

      // Re-evaluate 'Unknown' based on *both* constellation fields
      const currentConstellationInfo = mapToIAU(data.iauConstellation || data.constellation);
      const prevConstellationInfo = mapToIAU(prevObjRef.current?.iauConstellation || prevObjRef.current?.constellation);

      if (prevObjRef.current && currentConstellationInfo.full === "Unknown" && prevConstellationInfo.full === "Unknown") {
        if (!changedSignificantly(prevObjRef.current, data)) return;
      }

      // Only update if changed significantly to reduce re-renders
      if (!changedSignificantly(prevObjRef.current, data)) return;

      // fetch wiki only when name exists, with better fallback (opensearch)
      let description = data.description || "";
      let image = data.image || null;
      if (data && data.name) {
        try {
          const fetchWikiSummary = async (name) => {
            const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(name)}`;
            try {
              const r = await fetch(summaryUrl);
              if (r.ok) {
                const wd = await r.json();
                if (wd && (wd.extract || wd.thumbnail)) return wd;
              }
            } catch (e) {
              // continue to opensearch fallback
            }
            // opensearch fallback to find a better title
            try {
              const openUrl = `https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(name)}&limit=1&namespace=0&format=json&origin=*`;
              const or = await fetch(openUrl);
              if (or.ok) {
                const od = await or.json();
                const title = od && od[1] && od[1][0];
                if (title) {
                  const rr = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`);
                  if (rr.ok) return await rr.json();
                }
              }
            } catch (e) {
              // give up
            }
            return null;
          };

          const wikiData = await fetchWikiSummary(data.name);
          if (wikiData) {
            description = wikiData.extract || description;
            image = wikiData.thumbnail?.source || image || null;
          }
        } catch (e) {
          // non-fatal, just record
          console.warn("Wiki fetch failed", e);
        }
      }

      // When setting the object, we correctly retrieve properties using bracket notation
      const nextObj = {
        ...data,
        description,
        image,
        localizedName: data['localized-name'] || data.name, // Corrected access
        objectType: data['object-type'] || data.type // Corrected access
      };
      prevObjRef.current = nextObj;
      setObject(nextObj);
    } catch (err) {
      console.error("Error fetching celestial data:", err);
      setError("Failed to fetch celestial data. Check Stellarium or network.");
    }
  }, []);

  // polling effect
  useEffect(() => {
    if (!polling) return;
    fetchObject();
    const id = setInterval(fetchObject, intervalMs);
    return () => clearInterval(id);
  }, [fetchObject, polling, intervalMs]);

  // translate (debounced) whenever language or object.description changes
  useEffect(() => {
    if (!object || !object.description) {
      setTranslatedDesc("");
      return;
    }

    // clear existing timer
    if (translateTimerRef.current) clearTimeout(translateTimerRef.current);

    const doTranslate = async () => {
      setTranslating(true);
      setError(null);
      if (language === "en") {
        setTranslatedDesc(object.description);
        setTranslating(false);
        return;
      }

      const translateLongText = async (text, lang) => {
        const maxChunk = 450; // Max characters per request
        const apiUrl = "https://api.mymemory.translated.net/get";
        let fullTranslatedText = "";

        const makeApiCall = async (q) => {
          try {
            const r = await axios.get(apiUrl, {
              params: { q: q, langpair: `en|${lang}` },
              timeout: 15000, // Increased timeout
            });
            if (r.data.responseData?.translatedText && r.data.responseData.translatedText.trim() !== q.trim()) {
              return r.data.responseData.translatedText;
            } else if (r.data.responseStatus === 200 && r.data.responseData?.match === 0) {
                // If match is 0, it means no translation was found, often due to rate limits or invalid target language
                throw new Error("Translation API returned original text or failed (match 0).");
            }
            else {
              throw new Error(`Translation API returned original text or failed. Status: ${r.data.responseStatus}`);
            }
          } catch (e) {
            console.error("Translation API call failed for chunk:", q, e);
            throw e; // Re-throw to be caught by outer try-catch
          }
        };

        const parts = [];
        for (let i = 0; i < text.length; i += maxChunk) {
            parts.push(text.slice(i, i + maxChunk));
        }

        const translatedParts = [];
        for (const p of parts) {
            try {
                const translatedChunk = await makeApiCall(p);
                translatedParts.push(translatedChunk);
                await new Promise((res) => setTimeout(res, 300)); // Small delay between chunks
            } catch (e) {
                // If a chunk fails, we'll use the original chunk.
                // This means parts of the description might be translated, others not.
                translatedParts.push(p);
                console.warn("Could not translate chunk, using original:", p);
                // No need to set global error here, it will be handled by the check after loop
            }
        }
        fullTranslatedText = translatedParts.join(" ");

        // Check if the overall translated text is different from the original
        if (fullTranslatedText.trim() !== text.trim()) {
            return fullTranslatedText;
        } else {
            throw new Error("Overall translation result was identical to original text.");
        }
      };

      try {
        const translated = await translateLongText(object.description, language);
        setTranslatedDesc(translated);
      } catch (err) {
        console.error("Translation error:", err);
        setError("Translation failed. Displaying original English text. (API issue or rate limit?)");
        setTranslatedDesc(object.description); // Fallback to English
      } finally {
        setTranslating(false);
      }
    };

    translateTimerRef.current = setTimeout(doTranslate, 700);
    return () => clearTimeout(translateTimerRef.current);
  }, [language, object]);


  // --- Text-to-Speech Controls (multi-voice) ---
  const playText = (text, voiceName, langCode) => {
    if (!text) {
      setError("No text available to speak.");
      return;
    }
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      console.warn("Speech synthesis cancel failed:", e);
    }

    const u = new SpeechSynthesisUtterance(text);
    u.lang = langCode;

    const selected = voices.find((v) => v.name === voiceName);
    if (selected) {
      u.voice = selected;
    } else {
      const primaryLang = (langCode || "en-US").split("-")[0].toLowerCase();
      const foundByLang = voices.find(
        (v) => (v.lang || "").toLowerCase().startsWith(primaryLang)
      );
      if (foundByLang) {
        u.voice = foundByLang;
      } else {
        // If no voice for the specific language, try to find any English voice as a last resort
        const englishVoice = voices.find(v => (v.lang || "").toLowerCase().startsWith('en'));
        if (englishVoice) {
            u.voice = englishVoice;
            setError(`No suitable voice found for ${langCode}. Speaking with an English voice.`);
        } else {
            setError(`No suitable voice found for ${langCode}. Speaking with default browser voice.`);
        }
      }
    }

    u.onstart = () => setIsSpeaking(true);
    u.onend = () => setIsSpeaking(false);
    u.onerror = (e) => {
      console.error("TTS error", e);
      setError(`Speech synthesis error: ${e.error}. Try another voice or browser.`);
      setIsSpeaking(false);
    };
    window.speechSynthesis.speak(u);
    speechRefTranslated.current = u;
  };

  const handlePlayTranslated = () => {
    let textToSpeak = translatedDesc || object?.description || "";
    const langMap = {
      ml: "ml-IN", hi: "hi-IN", ta: "ta-IN", te: "te-IN", kn: "kn-IN", en: "en-US",
    };
    let lc = langMap[language] || "en-US"; // Default to en-US if language not in map

    // Determine if translation actually happened or if we're just displaying original English
    const isTranslationEffective = translatedDesc.trim() !== (object?.description || '').trim();

    if (!isTranslationEffective && language !== 'en') {
      // Translation failed or resulted in original text, and the target language isn't English.
      // So, we should speak the original English text using an English voice.
      lc = 'en-US';
      setError("Translation failed, reading original English text aloud.");
    } else if (isTranslationEffective && language !== 'en') {
      // Translation was successful and target language is not English, use target language voice.
      // The `lc` is already set to the target language.
      setError(null); // Clear previous error if translation is now successful
    } else if (language === 'en') {
        // We are intentionally in English mode, so use English voice.
        lc = 'en-US';
        setError(null);
    }

    playText(textToSpeak, selectedVoiceTranslated, lc);
  };


  const handlePause = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsSpeaking(false);
    }
  };

  const handleResume = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsSpeaking(true);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // Save to Backend (with error UI)
  const handleSave = async () => {
    if (!object) return;
    setError(null);
    try {
      await axios.post("http://localhost:5000/api/observations", {
        name: object.name,
        type: object.type,
        constellation: object.constellation,
        altitude: object.altitude,
        azimuth: object.azimuth,
        description: translatedDesc || object.description,
        language,
      });
      setError("Saved successfully 🌟");
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error("Error saving object:", err);
      setError("Error saving object. Check backend connection.");
    }
  };

  const imageFallback = (e) => {
    e.target.src =
      "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/480px-No_image_available.svg.png";
  };

  if (!object) {
    return (
      <div className={darkMode ? "cd-wrapper dark" : "cd-wrapper light"}>
        <div className="cd-card">
          <p className="muted">Loading celestial data...</p>
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    );
  }

  // Get formatted constellation
  const constellationInfo = mapToIAU(object.iauConstellation || object.constellation);

  // Determine if image is present
  const hasImage = object.image && object.image !== "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/480px-No_image_available.svg.png";


  return (
    <div className={darkMode ? "cd-wrapper dark" : "cd-wrapper light"}>
      <div className="cd-grid">
        <div className="cd-card card-header">
          <div className="cd-header">
            {/* Localized Name at the very top, large */}
            <h2>{object['localized-name'] || object.name || "-"}</h2>
          </div>

          <div className="meta-row">
            {/* Type */}
            <div><strong>Type:</strong> {object.type || object['object-type'] || "-"}</div>
            {/* Constellation */}
            <div>
              <strong>Constellation:</strong> {constellationInfo.full}{" "}
              {constellationInfo.trimmed && constellationInfo.full !== constellationInfo.trimmed && (
                `[${constellationInfo.trimmed}]`
              )}
            </div>
            {/* Azimuth and Altitude */}
            <div><strong>Azimuth:</strong> {object.azimuth != null ? object.azimuth.toFixed(8) + "°" : "-"}</div>
            <div><strong>Altitude:</strong> {object.altitude != null ? object.altitude.toFixed(8) + "°" : "-"}</div>
          </div>

          {/* Conditional image display */}
          {hasImage && (
            <div className="card-media">
              <img src={object.image} alt={object.name} onError={imageFallback} className="card-image" />
            </div>
          )}

          {/* English Description - always present now */}
          <div className="cd-english-desc">
            <h3 style={{ textAlign: 'center' }}>English Description</h3>
            <div className="desc-content">
              <p>{object.description || "No description available."}</p>
            </div>
          </div>
        </div>

        <div className="cd-section descriptions">
          <div className="cd-card card-desc">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Description ({getLangLabel(language)})</h3>
              <div>
                <label style={{ marginRight: 8, color: '#9CA3AF' }}>Language:</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="p-2 text-white transition bg-gray-800 border border-teal-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {LANG_OPTIONS.map(l => (
                    <option key={l.code} value={l.code} className="text-white bg-gray-900">
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="desc-content">
              {translating ? <p className="muted">Translating...</p> : <p>{translatedDesc || object.description || "No description available."}</p>}
            </div>

            {/* <div className="cd-tts small-tts">
              <label style={{ marginRight: 8, color: '#9CA3AF' }}>Voice:</label>
              <select
                value={selectedVoiceTranslated}
                onChange={(e) => setSelectedVoiceTranslated(e.target.value)}
                className="p-2 text-white transition bg-gray-800 border border-teal-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                <option value="">(Auto Detect)</option>
                {voices
                  .filter(v => (v.lang || "").toLowerCase().startsWith((language || "en").split("-")[0]))
                  .map((v) => (
                    <option key={v.name} value={v.name}>{`${v.name} — ${v.lang}`}</option>
                  ))}
                {voices
                  .filter(v => !(v.lang || "").toLowerCase().startsWith((language || "en").split("-")[0]))
                  .map((v) => (
                    <option key={v.name} value={v.name}>{`${v.name} — ${v.lang} (Other)`}</option>
                  ))}
              </select>
            </div>

            <div className="tts-buttons-below">
              <button onClick={handlePlayTranslated} disabled={isSpeaking || translating}>
                {isSpeaking ? "Speaking..." : "Play"}
              </button>
              <button onClick={handlePause} disabled={!isSpeaking && !window.speechSynthesis.paused}>Pause</button>
              <button onClick={handleResume} disabled={!window.speechSynthesis.paused}>Resume</button>
              <button onClick={handleStop} disabled={!isSpeaking && !window.speechSynthesis.paused}>Stop</button>
            </div> */}
          </div>
        </div>
      </div>
      {error && <div className="error bottom-error">{error}</div>}
    </div>
  );
}

export default CelestialDetails;